'use client';

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/utils/utils';
import { ToolHero } from '@/components/tools/tool-hero';
import { useRoiCalculatorStore } from '@/stores/roi-calculator-store';
import {
	ROI_BOOKING_URL,
	ROI_CONTACT_EMAIL,
	ROI_SLIDERS,
	ROI_TASK_CHIPS,
} from '@/data/tools/roi-calculator';
import {
	buildRoiSummary,
	computeRoi,
	equivalentLine,
	isValidEmail,
	money,
} from '@/lib/tools/roi';

export function RoiCalculator(): JSX.Element {
	const { toast } = useToast();
	const people = useRoiCalculatorStore((state) => state.people);
	const hours = useRoiCalculatorStore((state) => state.hours);
	const rate = useRoiCalculatorStore((state) => state.rate);
	const selectedTasks = useRoiCalculatorStore((state) => state.selectedTasks);
	const setPeople = useRoiCalculatorStore((state) => state.setPeople);
	const setHours = useRoiCalculatorStore((state) => state.setHours);
	const setRate = useRoiCalculatorStore((state) => state.setRate);
	const toggleTask = useRoiCalculatorStore((state) => state.toggleTask);

	const isEmailOpen = useRoiCalculatorStore((state) => state.isEmailOpen);
	const email = useRoiCalculatorStore((state) => state.email);
	const emailError = useRoiCalculatorStore((state) => state.emailError);
	const setEmailOpen = useRoiCalculatorStore((state) => state.setEmailOpen);
	const setEmail = useRoiCalculatorStore((state) => state.setEmail);
	const setEmailError = useRoiCalculatorStore((state) => state.setEmailError);

	const result = computeRoi({ people, hours, rate });

	const handleCopy = async (): Promise<void> => {
		const summary = buildRoiSummary(result, selectedTasks);
		try {
			await navigator.clipboard.writeText(summary);
		} catch {
			/* clipboard unavailable — still confirm so the flow feels responsive */
		}
		toast({ title: 'Results copied', description: 'Your ROI estimate is on the clipboard.' });
	};

	const handleSendEmail = (): void => {
		if (!isValidEmail(email)) {
			setEmailError(true);
			return;
		}
		const href = `mailto:${encodeURIComponent(email)}?cc=${encodeURIComponent(
			ROI_CONTACT_EMAIL,
		)}&subject=${encodeURIComponent('My automation ROI number')}&body=${encodeURIComponent(
			buildRoiSummary(result, selectedTasks),
		)}`;
		setEmailOpen(false);
		window.location.href = href;
	};

	return (
		<div className="flex flex-col">
			<ToolHero
				eyebrow="JovianCloudWorks · Automation ROI"
				title="Automation ROI Calculator"
				subtitle="Three numbers. See what manual work is actually costing you this year—and what you could reclaim by automating it."
			/>

			<div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">
				<div className="rounded-xl border border-border bg-card p-6 sm:p-8">
					<p className="mb-6 text-sm text-muted-foreground">
						Adjust the sliders to match your team. Results update instantly.
					</p>

					<SliderField
						label="People doing this manual work"
						value={people}
						display={String(people)}
						config={ROI_SLIDERS.people}
						onChange={setPeople}
					/>
					<SliderField
						label="Hours per week, per person, on repetitive/manual tasks"
						value={hours}
						display={String(hours)}
						config={ROI_SLIDERS.hours}
						onChange={setHours}
					/>
					<SliderField
						label="Fully loaded hourly cost"
						value={rate}
						display={money(rate)}
						config={ROI_SLIDERS.rate}
						onChange={setRate}
						hint="Rough rule of thumb: salary divided by 2,000, times about 1.25 to 1.4 for overhead and benefits."
					/>

					<div className="mb-6">
						<Label className="mb-2 block text-sm font-semibold">
							What kind of work is it? (optional, for context)
						</Label>
						<div className="flex flex-wrap gap-2">
							{ROI_TASK_CHIPS.map((task) => {
								const active = selectedTasks.includes(task);
								return (
									<button
										key={task}
										type="button"
										onClick={(): void => toggleTask(task)}
										className={cn(
											'rounded-full border px-3.5 py-2 text-sm transition-colors',
											active
												? 'border-orange-500 bg-orange-500/10 font-semibold text-foreground'
												: 'border-border text-muted-foreground hover:border-purple-500/60',
										)}
									>
										{task}
									</button>
								);
							})}
						</div>
					</div>

					<div className="rounded-xl bg-[#2f1b4e] p-7 text-center text-white">
						<p className="font-mono text-[11px] font-bold uppercase tracking-[0.06em] text-orange-400">
							What this costs you right now
						</p>
						<p className="mt-3 font-mono text-4xl font-bold leading-none">{money(result.annualCost)}</p>
						<p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-purple-200">
							per year on manual work
						</p>
						<div className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-white/15 bg-white/15">
							<div className="bg-white/[0.045] p-4">
								<b className="block font-mono text-[22px]">
									{Math.round(result.annualHours).toLocaleString('en-US')}
								</b>
								<span className="mt-1 block font-mono text-[10px] uppercase tracking-wide text-purple-200">
									Hours per year
								</span>
							</div>
							<div className="bg-white/[0.045] p-4">
								<b className="block font-mono text-[22px]">{money(result.weeklyCost)}</b>
								<span className="mt-1 block font-mono text-[10px] uppercase tracking-wide text-purple-200">
									Cost per week
								</span>
							</div>
						</div>
						<p className="mt-4 text-[13.5px] text-purple-100">{equivalentLine(result)}</p>
					</div>

					<div className="mt-6 flex flex-wrap justify-center gap-3">
						<Button asChild>
							<a href={ROI_BOOKING_URL} target="_blank" rel="noopener noreferrer">
								Talk about automating this
							</a>
						</Button>
						<Button variant="outline" onClick={(): void => setEmailOpen(true)}>
							Email me this number
						</Button>
						<Button variant="outline" onClick={handleCopy}>
							Copy results
						</Button>
					</div>

					<p className="mt-5 text-center text-xs text-muted-foreground">
						Rough figures to size the opportunity, not a formal quote. A real scope takes about 20
						minutes to nail down.
					</p>
				</div>
			</div>

			<Dialog
				open={isEmailOpen}
				onOpenChange={(open): void => {
					setEmailOpen(open);
					if (!open) setEmailError(false);
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Email me this number</DialogTitle>
						<DialogDescription>
							Enter your email and we&apos;ll open a message with your ROI estimate ready to send.
							JovianCloudWorks will be CC&apos;d so we can help you act on it.
						</DialogDescription>
					</DialogHeader>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="roi-email">Your email</Label>
						<Input
							id="roi-email"
							type="email"
							inputMode="email"
							autoComplete="email"
							placeholder="you@company.com"
							value={email}
							onChange={(event): void => {
								setEmail(event.target.value);
								if (emailError) setEmailError(false);
							}}
							onKeyDown={(event): void => {
								if (event.key === 'Enter') {
									event.preventDefault();
									handleSendEmail();
								}
							}}
						/>
						{emailError ? (
							<p className="text-xs text-destructive">Please enter a valid email address.</p>
						) : null}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={(): void => setEmailOpen(false)}>
							Cancel
						</Button>
						<Button onClick={handleSendEmail}>Send my results</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

type SliderFieldProps = {
	label: string;
	value: number;
	display: string;
	config: { min: number; max: number; step: number };
	onChange: (value: number) => void;
	hint?: string;
};

function SliderField({ label, value, display, config, onChange, hint }: SliderFieldProps): JSX.Element {
	return (
		<div className="mb-5">
			<div className="mb-2 flex items-center justify-between gap-3">
				<Label className="text-sm font-semibold">{label}</Label>
				<span className="font-mono text-sm font-bold text-orange-500">{display}</span>
			</div>
			<Slider
				min={config.min}
				max={config.max}
				step={config.step}
				value={[value]}
				onValueChange={(next): void => onChange(next[0])}
			/>
			{hint ? <p className="mt-2 text-xs text-muted-foreground">{hint}</p> : null}
		</div>
	);
}
